import type { Action, ActiveAction } from './action'
import { AREA_COLORS, type Area } from './area.ts'
import type { ActivityRecord, Milestone } from './history'
import type { Objective } from './objective'
import { isDeadline } from './objective.ts'
import type { RewardItem, RewardRedemption } from './reward'
import type { Specialization } from './specialization'

export const LEVELERIA_BACKUP_FORMAT = 'leveleria-backup'
export const LEVELERIA_BACKUP_SCHEMA = 1

export interface LeveleriaBackupData {
  areas: Area[]
  actions: Action[]
  specializations: Specialization[]
  objectives: Objective[]
  activityRecords: ActivityRecord[]
  milestones: Milestone[]
  rewards: RewardItem[]
  rewardRedemptions: RewardRedemption[]
  activeAction: ActiveAction | null
  coins: number
  targetRewardId: string | null
}

export interface LeveleriaBackup {
  format: typeof LEVELERIA_BACKUP_FORMAT
  schemaVersion: typeof LEVELERIA_BACKUP_SCHEMA
  appVersion: string
  exportedAt: string
  data: LeveleriaBackupData
}

export interface BackupSummary {
  areas: number
  actions: number
  activityRecords: number
  rewards: number
  rewardRedemptions: number
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)
const isString = (value: unknown): value is string => typeof value === 'string'
const isText = (value: unknown, maxLength: number): value is string => isString(value) && value.trim().length > 0 && value.length <= maxLength
const isId = (value: unknown): value is string => isText(value, 128)
const isNullableString = (value: unknown): value is string | null => value === null || isString(value)
const isSafeCount = (value: unknown): value is number => Number.isSafeInteger(value) && (value as number) >= 0
const isTimestamp = (value: unknown): value is number => Number.isSafeInteger(value) && (value as number) >= 0
const isNullableTimestamp = (value: unknown): value is number | null => value === null || isTimestamp(value)
const isStringArray = (value: unknown): value is string[] => Array.isArray(value) && value.every(isString)

const hasUniqueIds = (items: Array<{ id: string }>) => new Set(items.map((item) => item.id)).size === items.length

const isArea = (value: unknown): value is Area => isRecord(value)
  && isId(value.id) && isText(value.name, 60) && isString(value.color) && AREA_COLORS.includes(value.color as Area['color'])
  && isSafeCount(value.xp) && isNullableString(value.activeObjectiveName)
  && isTimestamp(value.createdAt) && isTimestamp(value.updatedAt)

const isAction = (value: unknown): value is Action => isRecord(value)
  && isId(value.id) && isId(value.areaId) && isStringArray(value.categoryIds) && isStringArray(value.objectiveIds)
  && isText(value.name, 80) && ['simple', 'timer', 'counter'].includes(String(value.type))
  && ['repeatable', 'once'].includes(String(value.repeatMode))
  && isSafeCount(value.xp) && value.xp >= 1 && value.xp <= 9_999_999 && isSafeCount(value.coins) && value.coins <= 9_999_999 && isSafeCount(value.completionCount)
  && isSafeCount(value.totalAmount) && isNullableTimestamp(value.completedAt)
  && isTimestamp(value.createdAt) && isTimestamp(value.updatedAt)

const isSpecialization = (value: unknown): value is Specialization => isRecord(value)
  && isId(value.id) && isId(value.areaId) && isText(value.name, 30)
  && isSafeCount(value.xp) && isTimestamp(value.createdAt)

const isObjective = (value: unknown): value is Objective => isRecord(value)
  && isId(value.id) && isId(value.areaId) && isText(value.name, 80)
  && ['active', 'later', 'completed', 'archived'].includes(String(value.status))
  && isSafeCount(value.priority) && isTimestamp(value.createdAt) && isTimestamp(value.updatedAt)
  && (value.closedProgress === undefined || (isRecord(value.closedProgress) && isSafeCount(value.closedProgress.completed) && isSafeCount(value.closedProgress.total) && value.closedProgress.completed <= value.closedProgress.total))
  && (value.deadline === undefined || value.deadline === null || isDeadline(value.deadline))
  && (value.originalDeadline === undefined || value.originalDeadline === null || isDeadline(value.originalDeadline))
  && (value.raceStartedAt === undefined || isTimestamp(value.raceStartedAt))
  && (value.notes === undefined || (Array.isArray(value.notes) && value.notes.every((note) => isRecord(note)
    && (note.actionId === undefined || note.actionId === null || isId(note.actionId))
    && isId(note.id) && isText(note.text, 2000) && isString(note.nextStep) && note.nextStep.length <= 200 && isTimestamp(note.createdAt))
    && hasUniqueIds(value.notes as Array<{ id: string }>)))

const isActivityRecord = (value: unknown): value is ActivityRecord => isRecord(value)
  && isId(value.id) && isId(value.areaId) && isText(value.areaName, 60)
  && isId(value.actionId) && isText(value.actionName, 80)
  && (value.objectiveIds === undefined || isStringArray(value.objectiveIds))
  && ['simple', 'timer', 'counter'].includes(String(value.type))
  && isSafeCount(value.amount) && isSafeCount(value.xp) && isSafeCount(value.coins)
  && isNullableTimestamp(value.startedAt) && isTimestamp(value.completedAt)

const isMilestone = (value: unknown): value is Milestone => isRecord(value)
  && isId(value.id) && isId(value.areaId) && isText(value.areaName, 60) && isText(value.title, 80)
  && ['objective', 'uniqueAction'].includes(String(value.kind))
  && isSafeCount(value.xp) && isSafeCount(value.coins) && isTimestamp(value.achievedAt)

const isReward = (value: unknown): value is RewardItem => isRecord(value)
  && isId(value.id) && isText(value.name, 60) && isSafeCount(value.cost) && value.cost >= 1 && value.cost <= 999_999_999
  && isSafeCount(value.redeemedCount) && isTimestamp(value.createdAt) && isTimestamp(value.updatedAt)

const isRedemption = (value: unknown): value is RewardRedemption => isRecord(value)
  && isId(value.id) && isId(value.rewardId) && isText(value.rewardName, 60)
  && isSafeCount(value.cost) && value.cost >= 1 && value.cost <= 999_999_999 && isSafeCount(value.coinsAfter) && isTimestamp(value.redeemedAt)

const isActiveAction = (value: unknown): value is ActiveAction => isRecord(value)
  && isId(value.actionId) && isId(value.areaId) && isSafeCount(value.amount) && value.amount >= 1 && value.amount <= 999_999_999
  && isTimestamp(value.startedAt)

const requireArray = <T>(value: unknown, guard: (item: unknown) => item is T): T[] => {
  if (!Array.isArray(value) || !value.every(guard)) throw new Error('El respaldo contiene datos incompletos o dañados.')
  return value
}

export const parseLeveleriaBackup = (value: unknown): LeveleriaBackup => {
  if (!isRecord(value) || value.format !== LEVELERIA_BACKUP_FORMAT || value.schemaVersion !== LEVELERIA_BACKUP_SCHEMA || !isText(value.appVersion, 30) || !isString(value.exportedAt) || !isRecord(value.data)) {
    throw new Error('El archivo no es un respaldo compatible de Leveleria.')
  }
  if (Number.isNaN(Date.parse(value.exportedAt))) throw new Error('El respaldo no tiene una fecha válida.')

  const data = value.data
  const backup: LeveleriaBackup = {
    format: LEVELERIA_BACKUP_FORMAT,
    schemaVersion: LEVELERIA_BACKUP_SCHEMA,
    appVersion: value.appVersion,
    exportedAt: value.exportedAt,
    data: {
      areas: requireArray(data.areas, isArea),
      actions: requireArray(data.actions, isAction),
      specializations: requireArray(data.specializations, isSpecialization),
      objectives: requireArray(data.objectives, isObjective),
      activityRecords: requireArray(data.activityRecords, isActivityRecord),
      milestones: requireArray(data.milestones, isMilestone),
      rewards: requireArray(data.rewards, isReward),
      rewardRedemptions: requireArray(data.rewardRedemptions, isRedemption),
      activeAction: data.activeAction === null ? null : isActiveAction(data.activeAction) ? data.activeAction : (() => { throw new Error('La Acción activa del respaldo no es válida.') })(),
      coins: isSafeCount(data.coins) ? data.coins : (() => { throw new Error('El saldo del respaldo no es válido.') })(),
      targetRewardId: isNullableString(data.targetRewardId) ? data.targetRewardId : (() => { throw new Error('La Recompensa objetivo del respaldo no es válida.') })(),
    },
  }

  const collections: Array<Array<{ id: string }>> = [backup.data.areas, backup.data.actions, backup.data.specializations, backup.data.objectives, backup.data.activityRecords, backup.data.milestones, backup.data.rewards, backup.data.rewardRedemptions]
  if (collections.some((items) => !hasUniqueIds(items))) throw new Error('El respaldo contiene identificadores duplicados.')

  const areaIds = new Set(backup.data.areas.map((item) => item.id))
  const specializationById = new Map(backup.data.specializations.map((item) => [item.id, item]))
  const objectiveById = new Map(backup.data.objectives.map((item) => [item.id, item]))
  const actionById = new Map(backup.data.actions.map((item) => [item.id, item]))
  const rewardIds = new Set(backup.data.rewards.map((item) => item.id))

  if (backup.data.specializations.some((item) => !areaIds.has(item.areaId)) || backup.data.objectives.some((item) => !areaIds.has(item.areaId))) {
    throw new Error('El respaldo contiene Especializaciones u Objetivos sin Área.')
  }
  const activeObjectivesByArea = new Map<string, number>()
  backup.data.objectives.filter((item) => item.status === 'active').forEach((item) => activeObjectivesByArea.set(item.areaId, (activeObjectivesByArea.get(item.areaId) ?? 0) + 1))
  if ([...activeObjectivesByArea.values()].some((count) => count > 1)) throw new Error('El respaldo contiene más de un Objetivo activo en la misma Área.')
  if (backup.data.actions.some((action) => !areaIds.has(action.areaId)
    || action.categoryIds.some((id) => specializationById.get(id)?.areaId !== action.areaId)
    || action.objectiveIds.some((id) => objectiveById.get(id)?.areaId !== action.areaId))) {
    throw new Error('El respaldo contiene relaciones de Acciones que no son válidas.')
  }
  if (backup.data.activeAction) {
    const action = actionById.get(backup.data.activeAction.actionId)
    if (!action || action.areaId !== backup.data.activeAction.areaId || action.completedAt !== null) throw new Error('La Acción activa del respaldo ya no está disponible.')
  }
  if (backup.data.targetRewardId && !rewardIds.has(backup.data.targetRewardId)) throw new Error('La Recompensa objetivo del respaldo no existe.')

  return backup
}

export const summarizeBackup = (backup: LeveleriaBackup): BackupSummary => ({
  areas: backup.data.areas.length,
  actions: backup.data.actions.filter((action) => action.completedAt === null).length,
  activityRecords: backup.data.activityRecords.length,
  rewards: backup.data.rewards.length,
  rewardRedemptions: backup.data.rewardRedemptions.length,
})
